import React, { useEffect, useState } from "react";
import { NAV, LINKS } from "../data/site";
import { Menu, X } from "lucide-react";

/**
 * Floating pill Header — inspired by the user reference screenshot but using
 * the site's dark-navy palette + Clash Display / Karvest typography.
 *
 * Layout (desktop):
 *   [ logo-icon-square ]   Work · Process · Services · FAQ · Contact   [ Book a Project ]
 *   ^ small rounded square containing ONLY the chat-bubble icon (no wordmark)
 *   ^ nav links centered
 *   ^ prominent solid purple-gradient pill on the right (dominant CTA)
 *
 * The whole header floats: it's a rounded pill centered horizontally with a
 * gap from the top of the page.
 */

const ArrowIcon = () => (
  <svg
    width="1em"
    height="1em"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.5"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <line x1="7" y1="17" x2="17" y2="7" />
    <polyline points="7 7 17 7 17 17" />
  </svg>
);

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const smoothTo = (e, target) => {
    e.preventDefault();
    setOpen(false);
    const el = document.querySelector(target);
    if (target === "#top" || !el) {
      window.scrollTo({ top: 0, behavior: "smooth" });
      return;
    }
    el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <div
      className="fixed left-1/2 -translate-x-1/2 z-50 w-full flex justify-center pointer-events-none"
      style={{
        top: "18px",
        paddingLeft: "16px",
        paddingRight: "16px",
      }}
    >
      {/* Floating pill */}
      <header
        className="pointer-events-auto flex items-center justify-between w-full max-w-[1180px] transition-all duration-300"
        style={{
          padding: "8px 8px 8px 16px",
          borderRadius: "999px",
          backgroundColor: scrolled
            ? "rgba(15,23,42,0.85)"
            : "rgba(20,22,47,0.55)",
          border: "1px solid rgba(139,92,246,0.28)",
          backdropFilter: "blur(14px)",
          WebkitBackdropFilter: "blur(14px)",
          boxShadow:
            "0 18px 44px rgba(0,0,0,0.35), inset 0 1px 0 rgba(255,255,255,0.04)",
          minHeight: "60px",
        }}
      >
        {/* LEFT — logo icon only, inside a rounded-square badge */}
        <a
          href="#top"
          onClick={(e) => smoothTo(e, "#top")}
          className="flex items-center shrink-0"
          aria-label="Clickory home"
        >
          <img
            src="/assets/brand/clickory-logo-full.png"
            alt="Clickory"
            data-testid="header-logo"
            style={{ height: 44, width: "auto", display: "block" }}
          />
        </a>

        {/* CENTER — nav */}
        <nav className="hidden md:flex items-center gap-8 lg:gap-10 mx-6">
          {NAV.map((n) => (
            <a
              key={n.label}
              href={n.target}
              onClick={(e) => smoothTo(e, n.target)}
              style={{
                fontFamily: "'SF Pro Display',sans-serif",
                fontWeight: 400,
                fontSize: "15px",
                letterSpacing: "-0.005em",
                color: "rgba(255,255,255,0.78)",
                transition: "color 0.2s ease",
                whiteSpace: "nowrap",
              }}
              onMouseEnter={(e) => (e.currentTarget.style.color = "#FFFFFF")}
              onMouseLeave={(e) =>
                (e.currentTarget.style.color = "rgba(255,255,255,0.78)")
              }
            >
              {n.label}
            </a>
          ))}
        </nav>

        {/* RIGHT — dominant CTA */}
        <a
          href={LINKS.bookProject}
          target="_blank"
          rel="noopener noreferrer"
          className="hidden md:inline-flex items-center justify-center gap-2 shrink-0"
          style={{
            padding: "12px 24px",
            borderRadius: "999px",
            background: "linear-gradient(135deg, #4F46E5 0%, #8B5CF6 100%)",
            color: "#FFFFFF",
            fontFamily: "'SF Pro Display',sans-serif",
            fontWeight: 400,
            fontSize: "15px",
            letterSpacing: "0.005em",
            boxShadow:
              "0 10px 28px rgba(79,70,229,0.45), inset 0 1px 0 rgba(255,255,255,0.14)",
            transition: "transform 0.2s ease, box-shadow 0.2s ease",
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.transform = "translateY(-1px)";
            e.currentTarget.style.boxShadow =
              "0 14px 34px rgba(79,70,229,0.55), inset 0 1px 0 rgba(255,255,255,0.18)";
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = "translateY(0)";
            e.currentTarget.style.boxShadow =
              "0 10px 28px rgba(79,70,229,0.45), inset 0 1px 0 rgba(255,255,255,0.14)";
          }}
        >
          <ArrowIcon />
          Book a Project
        </a>

        {/* Mobile toggle */}
        <button
          aria-label="Toggle menu"
          onClick={() => setOpen((v) => !v)}
          className="md:hidden inline-flex items-center justify-center shrink-0"
          style={{
            width: 42,
            height: 42,
            borderRadius: "14px",
            border: "1px solid rgba(139,92,246,0.32)",
            backgroundColor: "rgba(20,22,47,0.6)",
            color: "#FFFFFF",
          }}
        >
          {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
        </button>
      </header>

      {/* Mobile dropdown — sits directly below the pill */}
      {open && (
        <div
          className="md:hidden pointer-events-auto absolute w-full max-w-[1180px]"
          style={{
            top: "72px",
            left: "50%",
            transform: "translateX(-50%)",
            padding: "16px 20px",
            borderRadius: "22px",
            backgroundColor: "rgba(15,23,42,0.94)",
            border: "1px solid rgba(139,92,246,0.22)",
            backdropFilter: "blur(14px)",
            WebkitBackdropFilter: "blur(14px)",
            boxShadow: "0 22px 50px rgba(0,0,0,0.45)",
            marginLeft: "16px",
            marginRight: "16px",
            width: "calc(100% - 32px)",
          }}
        >
          <div className="flex flex-col gap-3">
            {NAV.map((n) => (
              <a
                key={n.label}
                href={n.target}
                onClick={(e) => smoothTo(e, n.target)}
                style={{
                  fontFamily: "'SF Pro Display',sans-serif",
                  fontWeight: 400,
                  fontSize: "17px",
                  letterSpacing: "-0.005em",
                  color: "rgba(255,255,255,0.9)",
                  padding: "6px 0",
                }}
              >
                {n.label}
              </a>
            ))}
            <a
              href={LINKS.bookProject}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center gap-2 mt-2"
              style={{
                padding: "12px 22px",
                borderRadius: "999px",
                background: "linear-gradient(135deg, #4F46E5 0%, #8B5CF6 100%)",
                color: "#FFFFFF",
                fontFamily: "'SF Pro Display',sans-serif",
                fontWeight: 400,
                fontSize: "15px",
                boxShadow:
                  "0 10px 28px rgba(79,70,229,0.45), inset 0 1px 0 rgba(255,255,255,0.14)",
                alignSelf: "flex-start",
              }}
            >
              <ArrowIcon />
              Book a Project
            </a>
          </div>
        </div>
      )}
    </div>
  );
}
